import React, { useCallback, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";

import Loading from "../../../../common/Loading";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";

import Button from "components/CustomButtons/Button";
import { Box, Divider, InputAdornment } from "@material-ui/core";

const validationSchema = Yup.object().shape({
  vendor: Yup.object().shape({
    name: Yup.string().required("Vendor Name is required"),
    address1: Yup.string().required("Vendor Address is required"),
    address2: Yup.string(),
    city: Yup.string().required("City Name is required"),
    state: Yup.string().required("State Name is required"),
    zip: Yup.string().required("Zip/Postal Code is required"),
    country: Yup.string().required("Country Name is required")
  }),
  amount: Yup.number().min(0, "Min value 0.").required("Amount is required")
});

const defaultValues = {
  vendor: {
    name: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zip: "",
    country: ""
  },
  amount: 0
};

const VendorDialog = ({ open, data, onClose, onCreate, onUpdate }) => {
  const { item, isCreating } = data;

  const [isDirty, setDirty] = useState(false);

  const initialValues = item.vendor
    ? { vendor: item.vendor, amount: item.amount }
    : defaultValues;

  const handleSubmit = useCallback(
    values => {
      setDirty(true);
      isCreating ? onCreate(values) : onUpdate(values);
    },
    [isCreating]
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Chapter Financial Information</DialogTitle>
      <DialogContent>
        <Typography>
          <strong>{isCreating ? "Add" : "Edit"} Vendor Data</strong>
        </Typography>
        <Box mb={3} />
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, touched, errors, handleChange, handleBlur, isValid }) => (
            <Form autoComplete="off">
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    error={Boolean(
                      touched.vendor &&
                        touched.vendor.name &&
                        errors.vendor &&
                        errors.vendor.name
                    )}
                    helperText={
                      touched.vendor &&
                      touched.vendor.name &&
                      errors.vendor &&
                      errors.vendor.name
                    }
                    label="Vendor Name"
                    variant="outlined"
                    name="vendor.name"
                    fullWidth
                    value={values.vendor.name}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    error={Boolean(
                      touched.vendor &&
                        touched.vendor.address1 &&
                        errors.vendor &&
                        errors.vendor.address1
                    )}
                    helperText={
                      touched.vendor &&
                      touched.vendor.address1 &&
                      errors.vendor &&
                      errors.vendor.address1
                    }
                    label="Vendor Address line 1"
                    variant="outlined"
                    name="vendor.address1"
                    fullWidth
                    value={values.vendor.address1}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    error={Boolean(
                      touched.vendor &&
                        touched.vendor.address2 &&
                        errors.vendor &&
                        errors.vendor.address2
                    )}
                    helperText={
                      touched.vendor &&
                      touched.vendor.address2 &&
                      errors.vendor &&
                      errors.vendor.address2
                    }
                    label="Vendor Address line 2"
                    variant="outlined"
                    name="vendor.address2"
                    fullWidth
                    value={values.vendor.address2}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <TextField
                    error={Boolean(
                      touched.vendor &&
                        touched.vendor.city &&
                        errors.vendor &&
                        errors.vendor.city
                    )}
                    helperText={
                      touched.vendor &&
                      touched.vendor.city &&
                      errors.vendor &&
                      errors.vendor.city
                    }
                    label="Vendor City"
                    variant="outlined"
                    name="vendor.city"
                    value={values.vendor.city}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <TextField
                    error={Boolean(
                      touched.vendor &&
                        touched.vendor.state &&
                        errors.vendor &&
                        errors.vendor.state
                    )}
                    helperText={
                      touched.vendor &&
                      touched.vendor.state &&
                      errors.vendor &&
                      errors.vendor.state
                    }
                    label="Vendor State/Prov"
                    variant="outlined"
                    name="vendor.state"
                    value={values.vendor.state}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <TextField
                    error={Boolean(
                      touched.vendor &&
                        touched.vendor.zip &&
                        errors.vendor &&
                        errors.vendor.zip
                    )}
                    helperText={
                      touched.vendor &&
                      touched.vendor.zip &&
                      errors.vendor &&
                      errors.vendor.zip
                    }
                    label="Vendor Zip/Postal Code"
                    variant="outlined"
                    name="vendor.zip"
                    value={values.vendor.zip}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <TextField
                    error={Boolean(
                      touched.vendor &&
                        touched.vendor.country &&
                        errors.vendor &&
                        errors.vendor.country
                    )}
                    helperText={
                      touched.vendor &&
                      touched.vendor.country &&
                      errors.vendor &&
                      errors.vendor.country
                    }
                    label="Vendor Country"
                    variant="outlined"
                    name="vendor.country"
                    value={values.vendor.country}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    error={Boolean(touched.amount && errors.amount)}
                    helperText={
                      touched.amount && errors.amount && "Enter a valid number."
                    }
                    label="Amount"
                    variant="outlined"
                    name="amount"
                    fullWidth
                    value={values.amount}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      )
                    }}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>
              <Box display="flex" justifyContent="center" mt={3}>
                {isDirty ? (
                  <Loading />
                ) : (
                  <>
                    <Button color="success" variant="contained" type="submit">
                      Submit
                    </Button>
                    <Box mr={3} />
                    <Button
                      color="danger"
                      variant="contained"
                      onClick={onClose}
                    >
                      Cancel
                    </Button>
                  </>
                )}
              </Box>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default VendorDialog;
